/**
 * RoomItemsButtons + WallAndFloorAddOns — native staging add-on buttons (lifted
 * from `common/StagingFlow/RoomItems/RoomItemsButtons.component.tsx`, rewired onto
 * the module working context + `patchRoom` + plain-JS `renovations` pairs /
 * `roomTypes` array).
 *
 * The legacy renovation *price* (`renovationProductStringPrice`, from the
 * session-keyed renovation-product fetch that backs the DROPPED v2 cart-extra) is
 * gone — the no-price title variants are rendered. Analytics dropped.
 */

import { CSSProperties, FC, Fragment, ReactNode, useCallback, useEffect, useMemo } from 'react'

import { BEIGE_600, GRAY_700, GRAY_900, YELLOW_600 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'
import { VirtualFurnitureRoomRemovableOption, VirtualFurnitureRoomType } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'

import BedRoundedIcon from '@mui/icons-material/BedRounded'
import BorderAllRoundedIcon from '@mui/icons-material/BorderAllRounded'
import { CircleIcon } from 'components/common/CircleIcon'
import { Error } from '@mui/icons-material'
import LightRoundedIcon from '@mui/icons-material/LightRounded'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import { StagingType } from 'models/virtualStaging'
import { ToggleButton } from 'components/common/ToggleButton'

import { RoomItemPopupType, useStagingWorkingState } from '../StagingWorkingState.context'
import { mapDelete, mapGet, mapSet } from '../stagingRoom.logic'

const Subtext: FC<{ children: ReactNode; color?: string; weight?: CSSProperties['fontWeight'] }> = ({
  children,
  color = GRAY_700,
  weight,
}) => (
  <span style={{ fontSize: '1.4rem', color, fontWeight: weight }}>
    {children}
  </span>
)

interface WallAndFloorAddOnsProps {
  isWallSelected: boolean
  isFloorSelected: boolean
}

const WallAndFloorAddOns: FC<WallAndFloorAddOnsProps> = ({ isWallSelected, isFloorSelected }) => {
  const { t } = useTranslation(['style_selection_order_step', 'common'])
  const { activeRoom, isBKBNDecide, openRoomItemPopup, patchRoom } = useStagingWorkingState()

  const handleToggleButton = useCallback(
    (popupType: RoomItemPopupType, renovationType: VirtualFurnitureRoomType) => {
      if (!activeRoom) return
      if (isBKBNDecide) {
        if (mapGet(activeRoom.renovations, renovationType)) {
          patchRoom(activeRoom.key, { renovations: mapDelete(activeRoom.renovations, renovationType) })
          return
        }
        patchRoom(activeRoom.key, { renovations: mapSet(activeRoom.renovations, renovationType, StagingType.BKBN) })
        return
      }
      openRoomItemPopup(popupType)
    },
    [activeRoom, isBKBNDecide, openRoomItemPopup, patchRoom]
  )

  const handleNoneSelected = useCallback(() => {
    if (!activeRoom) return
    patchRoom(activeRoom.key, { renovations: [], skipRenovations: !activeRoom.skipRenovations })
  }, [activeRoom, patchRoom])

  const showNone = useMemo(() => {
    if (!activeRoom) return false
    const isEmptyRoom =
      activeRoom.roomTypes.length === 1 && activeRoom.roomTypes.includes(VirtualFurnitureRoomType.EMPTY_ROOM)
    if (isEmptyRoom && activeRoom.furnitureRemoval === VirtualFurnitureRoomRemovableOption.NONE) return false
    return true
  }, [activeRoom])

  // Clear `skipRenovations` once a renovation is chosen. VALUE-GUARDED: only patch
  // on a genuine flip — `patchRoom` calls the value-blind `markConfigDirty`, so an
  // unconditional effect-driven patch would un-confirm a saved config on churn.
  useEffect(() => {
    if (!activeRoom) return
    if (activeRoom.renovations.length > 0 && activeRoom.skipRenovations) {
      patchRoom(activeRoom.key, { skipRenovations: false })
    }
  }, [activeRoom, patchRoom])

  if (!activeRoom) return null

  return (
    <Fragment>

      {showNone &&
        <ToggleButton
          isSelected={activeRoom.skipRenovations}
          label={t('common:None')}
          sx={{ width: 'auto' }}
          onClick={handleNoneSelected}
        />
      }

      <ToggleButton
        isToggled={isWallSelected}
        label={t('style_selection_order_step:room_items.WALL')}
        icon={<CircleIcon size="3.2rem" circleColor={BEIGE_600} icon={<PaletteOutlinedIcon fontSize="large" />} />}
        onClick={() => handleToggleButton(RoomItemPopupType.WALL, VirtualFurnitureRoomType.WALL_MATERIALS)}
      />

      <ToggleButton
        isToggled={isFloorSelected}
        label={t('style_selection_order_step:room_items.FLOOR')}
        icon={<CircleIcon size="3.2rem" circleColor={BEIGE_600} icon={<BorderAllRoundedIcon fontSize="large" />} />}
        onClick={() => handleToggleButton(RoomItemPopupType.FLOOR, VirtualFurnitureRoomType.FLOORING)}
      />

    </Fragment>
  )
}

export const RoomItemsButtons: FC = () => {
  const { t } = useTranslation(['style_selection_order_step', 'common'])

  const {
    activeRoom,
    isSomeFurnitureSelected,
    isAllFurnitureSelected,
    isDecorationSelected,
    isWallSelected,
    isFloorSelected,
    isRoomEmptyType,
    isBKBNDecide,
    openRoomItemPopup,
  } = useStagingWorkingState()

  const hasSelectedRemovalOption = activeRoom?.furnitureRemoval !== VirtualFurnitureRoomRemovableOption.NONE

  // No session price → render the no-price title variants.
  const bkbnDecideTitle = t('style_selection_order_step:wall_floor_question')
  const advancedOptionTitle = t('style_selection_order_step:add_object_to_room')

  const displayAddons = useMemo(() => {
    if (isRoomEmptyType) return true
    if (!isBKBNDecide && hasSelectedRemovalOption) return false
    return !hasSelectedRemovalOption || isBKBNDecide
  }, [hasSelectedRemovalOption, isBKBNDecide, isRoomEmptyType])

  if (!activeRoom) return null

  return (
    <Stack gap="2.4rem" justifyContent="center" alignItems="center" paddingTop={1.8} maxWidth="90%">

      {!isRoomEmptyType && !isBKBNDecide &&
        <Stack gap="1.6rem" alignItems="center" width="100%">

          <Typography variant="text-md" fontWeight="semiBold" color={GRAY_900}>
            {advancedOptionTitle}
          </Typography>

          {!isRoomEmptyType &&
            <Stack direction="row" flexWrap="wrap" gap={0.8}>

              <ToggleButton
                isToggled={isSomeFurnitureSelected}
                toggleIcon={!isAllFurnitureSelected ? <Error color="warning" /> : undefined}
                label={
                  <span>
                    {t('style_selection_order_step:room_items.FURNITURE')}
                    {!isRoomEmptyType && <>&nbsp;<Subtext color={YELLOW_600} weight="bold">({t('style_selection_order_step:mandatory')})</Subtext></>}
                  </span>
                }
                sx={{ width: 'auto' }}
                icon={<CircleIcon size="3.2rem" circleColor={BEIGE_600} icon={<BedRoundedIcon fontSize="large" />} />}
                onClick={() => openRoomItemPopup(RoomItemPopupType.FURNITURE)}
              />

              <ToggleButton
                isToggled={isDecorationSelected}
                label={t('style_selection_order_step:room_items.DECORATION')}
                icon={<CircleIcon size="3.2rem" circleColor={BEIGE_600} icon={<LightRoundedIcon fontSize="large" />} />}
                onClick={() => openRoomItemPopup(RoomItemPopupType.DECORATION)}
              />

            </Stack>
          }

          {hasSelectedRemovalOption &&
            <Fragment>

              <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900}>
                {t('style_selection_order_step:advanced_staging_options_no_price')} <Subtext color={YELLOW_600} weight="bold">({t('style_selection_order_step:mandatory')})</Subtext>
              </Typography>

              <Stack direction="row" flexWrap="wrap" gap={0.8}>
                <WallAndFloorAddOns isWallSelected={isWallSelected} isFloorSelected={isFloorSelected} />
              </Stack>

            </Fragment>
          }

        </Stack>
      }

      {displayAddons &&
        <Stack gap="1.6rem" alignItems="center" width="100%">

          <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900} marginBottom={0.8}>
            {isBKBNDecide ? bkbnDecideTitle : advancedOptionTitle} <Subtext color={YELLOW_600} weight="bold">({t('style_selection_order_step:mandatory')})</Subtext>
          </Typography>

          {/* Wraps, like the identical row above it: three labelled buttons do not
              fit the editor panel once the content column narrows (they hung 90px
              past it at a 900px viewport, where the summary column first appears
              and the column is at its narrowest). */}
          <Stack flexDirection="row" flexWrap="wrap" gap="0.8rem" justifyContent="center">
            <WallAndFloorAddOns isWallSelected={isWallSelected} isFloorSelected={isFloorSelected} />
          </Stack>

        </Stack>
      }

    </Stack>
  )
}
